import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import React, { useState, useEffect, useRef } from "react";

import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import { Box, TextareaAutosize } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import Title from "../../../common/Title";
import InputFieldBox from "../../../common/InputFieldBox";

import useAlert from "../../../../hooks/useAlert";
import useAxios from "../../../../hooks/useAxios";
import { __activateTmobilej } from "../../../../api/sim";
import { __fetchPlans } from "../../../../api/plan";
import InputFieldAutoComplete from "../../../common/InputFieldAutoComplete";

const TmobilejActivateForm = () => {
  const [formData, setFormData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rawData, setRawData] = useState("");
  const [locationData, setLocationData] = useState([]);
  const [locationType, setLocationType] = useState(null); // 'areacode' or 'zipcode'
  const [plans, setPlans] = useState([]);
  const responseRef = useRef(null);

  const { setAlertMessage } = useAlert();
  const [response, call, loading] = useAxios({
    ...__activateTmobilej,
  });
  const [planResponse, planCall, planLoading] = useAxios({
    ...__fetchPlans,
  });
  const nav = useNavigate();

  const initialData = {
    plan_id: null,
    requested_lines: 0,
    areacode: "",
    zipcode: "",
    skip_zip_checks: false,
  };

  const validateFunction = (data, requestedLines) => {
    const errors = {};
    const formattedLines = [];
    let detectedType = null;

    if (!data || !data.trim()) {
      setLocationData([]);
      setLocationType(null);
      return errors;
    }

    const lines = data.split("\n").map(line => line.trim()).filter(line => line.length > 0);

    // If no lines provided, that's okay - locations are optional
    if (lines.length === 0) {
      setLocationData([]);
      setLocationType(null);
      return errors;
    }

    // Check if count exceeds requested_lines
    if (requestedLines && lines.length > requestedLines) {
      errors.locations = `Number of lines (${lines.length}) cannot exceed requested lines (${requestedLines})`;
      return errors;
    }

    // Validate each line
    lines.some((line, idx) => {
      const trimmedLine = line.trim();
      
      if (!trimmedLine) {
        return false; // Skip empty lines
      }

      // Check if line contains comma (ICCID with location)
      if (trimmedLine.includes(",")) {
        const parts = trimmedLine.split(",").map(part => part.trim()).filter(part => part.length > 0);
        
        if (parts.length > 2) {
          errors.locations = `Invalid format in line ${idx + 1}: ${trimmedLine}. Format should be: ICCID,areacode or ICCID,zipcode or just areacode/zipcode.`;
          return true;
        }

        const firstPart = parts[0];
        const secondPart = parts[1];

        // Validate ICCID (19-20 digits)
        if (!/^\d{19,20}$/.test(firstPart)) {
          errors.locations = `Invalid ICCID in line ${idx + 1}: ${firstPart}. ICCID must be 19 or 20 digits.`;
          return true;
        }

        // Validate second part (area code or zip code)
        if (secondPart) {
          if (/^\d{3}$/.test(secondPart)) {
            // It's an area code
            if (detectedType === null) {
              detectedType = "areacode";
            } else if (detectedType !== "areacode") {
              errors.locations = `Cannot mix area codes and zip codes. Line ${idx + 1} contains an area code but previous lines contain zip codes.`;
              return true;
            }
            formattedLines.push({ iccid: firstPart, areacode: secondPart.trim() });
          } else if (/^\d{5}$/.test(secondPart)) {
            // It's a zip code
            if (detectedType === null) {
              detectedType = "zipcode";
            } else if (detectedType !== "zipcode") {
              errors.locations = `Cannot mix area codes and zip codes. Line ${idx + 1} contains a zip code but previous lines contain area codes.`;
              return true;
            }
            formattedLines.push({ iccid: firstPart, zipcode: secondPart.trim() });
          } else {
            errors.locations = `Invalid location format in line ${idx + 1}: ${secondPart}. Area codes must be 3 digits and zip codes must be 5 digits.`;
            return true;
          }
        } else {
          // Only ICCID provided
          formattedLines.push({ iccid: firstPart });
        }
        return false;
      }

      // Check if it's a 3-digit area code (standalone)
      if (/^\d{3}$/.test(trimmedLine)) {
        if (detectedType === null) {
          detectedType = "areacode";
        } else if (detectedType !== "areacode") {
          errors.locations = `Cannot mix area codes and zip codes. Line ${idx + 1} contains an area code but previous lines contain zip codes.`;
          return true;
        }
        formattedLines.push({ areacode: trimmedLine.trim() });
        return false;
      }
      
      // Check if it's a 5-digit zip code (standalone)
      if (/^\d{5}$/.test(trimmedLine)) {
        if (detectedType === null) {
          detectedType = "zipcode";
        } else if (detectedType !== "zipcode") {
          errors.locations = `Cannot mix area codes and zip codes. Line ${idx + 1} contains a zip code but previous lines contain area codes.`;
          return true;
        }
        formattedLines.push({ zipcode: trimmedLine.trim() });
        return false;
      }

      // Check if it's an ICCID (19-20 digits, standalone)
      if (/^\d{19,20}$/.test(trimmedLine)) {
        formattedLines.push({ iccid: trimmedLine });
        return false;
      }
      
      // Invalid format
      errors.locations = `Invalid format in line ${idx + 1}: ${trimmedLine}. Format should be: ICCID (19-20 digits), area code (3 digits), zip code (5 digits), or ICCID,areacode/zipcode.`;
      return true;
    });

    if (!errors.locations) {
      setLocationData(formattedLines);
      setLocationType(detectedType);
    }

    return errors;
  };

  const validationSchema = yup
    .object({
      plan_id: yup
        .number("plan_id")
        .required("Plan is required")
        .nullable(),
      requested_lines: yup
        .number("requested_lines")
        .required("Requested lines is required")
        .min(1, "Requested lines must be at least 1")
        .integer("Requested lines must be a whole number"),
      skip_zip_checks: yup.boolean("skip_zip_checks").required(),
      locations: yup.string().optional(),
    });

  const formik = useFormik({
    initialValues: { ...initialData },
    onSubmit: (values) => {
      // locationData now contains objects with iccid, areacode, or zipcode
      // Only include lines if there are locations provided
      const lines = locationData.length > 0 ? [...locationData] : [];

      // Find the selected plan to get plan_name
      const plan = plans.find(p => p.id === values.plan_id);
      
      const requestData = {
        plan_id: values.plan_id,
        plan_name: plan?.plan_name || plan?.name || null,
        requested_lines: values.requested_lines,
        ...(lines.length > 0 && { lines: lines }),
        skip_zip_checks: values.skip_zip_checks,
      };

      setFormData(requestData);
    },
    validationSchema: validationSchema,
    validateOnChange: true,
  });

  useEffect(() => {
    planCall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (planResponse && planResponse.status) {
      setPlans(planResponse.data.data || planResponse.data);
    }
  }, [planResponse]);

  useEffect(() => {
    if (formData && !isSubmitting) {
      setIsSubmitting(true);
      formik.setSubmitting(true);
      const apiPayload = {
        plan_id: formData.plan_id,
        plan_name: formData.plan_name,
        requested_lines: formData.requested_lines,
        skip_zip_checks: formData.skip_zip_checks,
      };
      
      // Only include lines if there are locations provided
      if (formData.lines && formData.lines.length > 0) {
        apiPayload.lines = formData.lines;
      }
      
      call({
        data: apiPayload,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, call, isSubmitting]);

  useEffect(() => {
    if (!response || !isSubmitting) return;
    
    // Check if this is a new response (different from the last processed one)
    if (responseRef.current && 
        responseRef.current.status === response.status && 
        JSON.stringify(responseRef.current.data) === JSON.stringify(response.data)) {
      return;
    }

    if (response.status) {
      setAlertMessage({ type: "success", message: "Activation queue started" });
      nav("/tmobilej/activate");
    }

    // Store this response as the last processed one
    responseRef.current = response;
    
    // Reset states after processing response
    formik.setSubmitting(false);
    setIsSubmitting(false);
    setFormData(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response, nav, setAlertMessage, isSubmitting]);

  if (loading || planLoading) {
    return <Title content="Please wait..." variant="subtitle2" gutterBottom />;
  }

  return (
    <>
      <Box
        sx={{ width: "100%" }}
        display="flex"
        alignItems="center"
        flexDirection="column"
      >
        <Title
          variant="h5"
          content="Activate new sims"
          sx={{ mb: 3 }}
          gutterBottom
        />
        <Grid
          sx={{ flexGrow: 1 }}
          container
          spacing={2}
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item xs={12} minWidth={350}>
            <FormControl
              error={
                formik.touched.plan_id && Boolean(formik.errors.plan_id)
                  ? true
                  : false
              }
              sx={{ width: "100%" }}
            >
              <InputFieldAutoComplete
                isSelect
                options={[
                  ...plans.reduce((acc, plan) => {
                    const val = {
                      value: plan.id,
                      label: plan.plan_name || plan.name,
                    };
                    acc.push(val);
                    return acc;
                  }, []),
                ]}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                name="plan_id"
                label="Plan"
                id="plan_id"
                onChange={(e, value) => {
                  formik.setFieldValue("plan_id", value?.value || null);
                }}
              />
              {formik.touched.plan_id && Boolean(formik.errors.plan_id) ? (
                <FormHelperText id="plan_id-error-text">
                  {formik.errors.plan_id}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl
              error={Boolean(formik.errors.requested_lines) ? true : false}
              sx={{ width: "300px" }}
            >
              <InputFieldBox
                value={formik.values.requested_lines}
                onChange={(event) => {
                  const value = event.target.value;
                  formik.setFieldValue("requested_lines", value);
                  // Re-validate locations when requested_lines changes
                  if (rawData) {
                    const errors = validateFunction(rawData, value ? parseInt(value) : 0);
                    formik.setErrors({ ...formik.errors, locations: errors.locations });
                  }
                }}
                label="Requested Lines"
                type="number"
                placeholder="Enter number of lines to activate"
              />

              {Boolean(formik.errors.requested_lines) ? (
                <FormHelperText id="requested_lines-error-text">
                  {formik.errors.requested_lines}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <InputLabel htmlFor="locations">
              {"Add ICCID, area codes, or zip codes (optional, one per line):"}
              <br></br>
              {locationType === "areacode" ? (
                <>
                  {"89012345678901234567,123 (ICCID with area code)"}
                  <br></br>
                  {"123 (area code only)"}
                  <br></br>
                  <em>{"Note: ICCID must be 19-20 digits. Area codes must be 3 digits."}</em> 
                  <br></br>
                  <em>{"Use format: ICCID,areacode or just areacode. Number of lines cannot exceed requested lines. This field is optional."}{" "}</em>
                </>
              ) : locationType === "zipcode" ? (
                <>
                  {"89012345678901234567,12345 (ICCID with zip code)"}
                  <br></br>
                  {"12345 (zip code only)"}
                  <br></br>
                  <em>{"Note: ICCID must be 19-20 digits. Zip codes must be 5 digits."}</em> 
                  <br></br>
                  <em>{"Use format: ICCID,zipcode or just zipcode. Number of lines cannot exceed requested lines. This field is optional."}{" "}</em>
                </>
              ) : (
                <>
                  {"89012345678901234567,123 (ICCID with area code)"}
                  <br></br>
                  {"89012345678901234567,12345 (ICCID with zip code)"}
                  <br></br>
                  {"123 (area code only) or 12345 (zip code only)"}
                  <br></br>
                  {"89012345678901234567 (ICCID only)"}
                  <br></br>
                  <em>{"Note: ICCID must be 19-20 digits. Use either all area codes (3 digits) OR all zip codes (5 digits), not both."}</em> 
                  <br></br>
                  <em>{"Format: ICCID,areacode or ICCID,zipcode or just areacode/zipcode/ICCID. Number of lines cannot exceed requested lines. This field is optional."}{" "}</em>
                </>
              )}
            </InputLabel>
            <FormControl
              error={Boolean(formik.errors.locations) ? true : false}
              sx={{ width: "100%" }}
            >
              <TextareaAutosize
                multiline
                rows={6}
                aria-label="minimum height"
                placeholder={locationType === "areacode" 
                  ? "Add ICCID with area codes or area codes only (optional)\n89012345678901234567,123\n456" 
                  : locationType === "zipcode"
                  ? "Add ICCID with zip codes or zip codes only (optional)\n89012345678901234567,12345\n67890"
                  : "Add ICCID,areacode OR ICCID,zipcode OR areacode/zipcode/ICCID only (optional)\n89012345678901234567,123\n12345"}
                style={{ minWidth: 550 }}
                name="locations"
                minRows={5}
                id="locations"
                value={rawData}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const data = e.target.value;
                  setRawData(data);
                  const errors = validateFunction(data, formik.values.requested_lines);
                  formik.setErrors({ ...formik.errors, locations: errors.locations });
                }}
              />
              {Boolean(formik.errors.locations) ? (
                <FormHelperText id="locations-error-text">
                  {formik.errors.locations}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl
              error={Boolean(formik.errors.skip_zip_checks) ? true : false}
              sx={{ width: "300px" }}
            >
              <InputFieldBox
                isCheckbox={true}
                value={formik.values.skip_zip_checks}
                onChange={(event) =>
                  formik.setFieldValue("skip_zip_checks", event.target.checked)
                }
                label="Skip Zip checks"
              />

              {Boolean(formik.errors.skip_zip_checks) ? (
                <FormHelperText id="skip_zip_checks-error-text">
                  {formik.errors.skip_zip_checks}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid
            container
            sx={{ mt: 3 }}
            direction="row"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item>
              <LoadingButton
                style={{ marginRight: "5px" }}
                disabled={
                  formik.errors.requested_lines ||
                  !formik.values.requested_lines ||
                  !formik.values.plan_id ||
                  formik.errors.plan_id ||
                  formik.errors.locations ||
                  formik.isSubmitting
                }
                onClick={() =>
                  !loading && !formik.isSubmitting && !planLoading && formik.submitForm()
                }
                size="large"
                loading={loading}
                variant="contained"
              >
                {"Activate new SIMs"}
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default TmobilejActivateForm;
